/**
 * "Form" link next to an exercise. Opens a YouTube search by default; long-press-free
 * pinning lets you replace it with a specific video once you have watched one you trust.
 *
 * These need a signal, so they are for before you leave the house, not for the gym.
 */
import { useState } from 'react';
import type { StoreApi } from './useStore.ts';
import type { Exercise } from '../plan/types.ts';
import { videoFor, videoKey, isSafeVideoUrl } from '../plan/videos.ts';

export function VideoLink({ api, ex, allowPin = true }: {
  api: StoreApi; ex: Exercise; allowPin?: boolean;
}) {
  const link = videoFor(api.state, ex);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!link) return null;
  const key = videoKey(ex.n);

  const save = async () => {
    const v = draft.trim();
    if (v === '') { await api.del(key); setEditing(false); setErr(null); return; }
    if (!isSafeVideoUrl(v)) {
      setErr('That is not an https YouTube link. Paste the address from the video’s Share button.');
      return;
    }
    const ok = await api.put(key, v);
    if (ok) { setEditing(false); setDraft(''); setErr(null); }
    else setErr('Not saved — the write did not return successfully.');
  };

  return (
    <span className="vid">
      <a className="vidlink" href={link.url} target="_blank" rel="noopener noreferrer"
         aria-label={`How to do ${ex.n}, opens YouTube in a new tab`}>
        ▶ {link.pinned ? 'Form video' : 'Find form video'}
      </a>
      {allowPin && !editing && (
        <button className="linkbtn vidpin" disabled={!api.durable}
                onClick={() => { setDraft(link.pinned ? link.url : ''); setEditing(true); }}>
          {link.pinned ? 'change' : 'pin one'}
        </button>
      )}
      {editing && (
        <span className="videdit">
          <input type="url" inputMode="url" value={draft} placeholder="https://youtu.be/…"
                 aria-label={`Pinned form video for ${ex.n}`}
                 onChange={(e) => { setDraft(e.target.value); setErr(null); }} />
          <span className="btns" style={{ marginTop: 6 }}>
            <button className="small primary" disabled={api.pending.has(key)}
                    onClick={() => void save()}>
              {api.pending.has(key) ? 'Saving…' : 'Save'}
            </button>
            {link.pinned && (
              <button className="small" onClick={() => void api.del(key).then(() => setEditing(false))}>
                Unpin
              </button>
            )}
            <button className="small" onClick={() => { setEditing(false); setErr(null); }}>Cancel</button>
          </span>
          {err && <span className="errline">{err}</span>}
          {api.errorFor(key) && <span className="errline">Not saved — {api.errorFor(key)}</span>}
        </span>
      )}
    </span>
  );
}
